import { __decorate } from "tslib";
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Graph from './Graph.vue';
import { ESensor, ESetPoint, ETimeFrame, } from '@sicfe/rooms/types';
import { Notify } from 'quasar';
let SensorGraph = class SensorGraph extends Vue {
    constructor() {
        super(...arguments);
        this.loading = true;
        this.series = {};
        this.hasData = true;
    }
    title(key) {
        if (key in ESetPoint) {
            return this.$t(`roomText.setpoints.${key}`);
        }
        return this.$t(`roomText.sensors.${key}`);
    }
    emitZoom(event) {
        this.$emit('zoom', event);
    }
    getDebug(s) {
        return this.debug || s === ESensor.CurrentPowerPhase || s === ESensor.TotalPowerPhase;
    }
    async dataSourceChanged() {
        clearInterval(this.polling);
        this.loading = true;
        await this.updateGraphData();
        this.loading = false;
        this.hasData = Object.keys(this.series).length > 0;
        this.startPolling();
    }
    async getSeries(key) {
        if (key in ESetPoint) {
            const data = await this.getSetpointData(key);
            if (data.length === 0) {
                return [];
            }
            return [this.getRoomSensorDataSerie(key, data)];
        }
        if (this.getDebug(key)) {
            const data = await this.getRawRoomSensorData(key);
            if (data.length === 0) {
                return [];
            }
            return this.getRawRoomSensorDataSeries(key, data);
        }
        const data = await this.getRoomSensorData(key);
        if (data.length === 0) {
            return [];
        }
        const serie = this.getRoomSensorDataSerie(key, data);
        return [serie];
    }
    keyExistInRoom(key) {
        if (key in ESensor) {
            return !(!this.room.available_sensors || !this.room.available_sensors.includes(key));
        }
        else {
            return !(!this.room.current_setpoints || !(key in this.room.current_setpoints));
        }
    }
    async updateGraphData() {
        if (this.settings?.group) {
            for (const key of this.settings?.group) {
                if (!this.keyExistInRoom(key)) {
                    continue;
                }
                const data = await this.getSeries(key);
                if (!data || !data.length) {
                    this.$delete(this.series, key);
                }
                else {
                    this.$set(this.series, key, data);
                }
            }
        }
        const series = await this.getSeries(this.k);
        if (this.settings?.merge) {
            for (const key of this.settings?.merge) {
                if (!this.keyExistInRoom(key)) {
                    continue;
                }
                const serie = await this.getSeries(key);
                if (serie) {
                    series.push(...serie);
                }
            }
        }
        if (!series || !series.length) {
            this.$delete(this.series, this.k);
        }
        else {
            this.$set(this.series, this.k, series);
        }
    }
    async created() {
        this.loading = true;
        await this.updateGraphData();
        this.loading = false;
        this.hasData = Object.keys(this.series).length > 0;
        this.startPolling();
    }
    beforeDestroy() {
        clearInterval(this.polling);
    }
    startPolling() {
        this.polling = setInterval(async () => {
            if (!this.loading && this.datasource.timeframe === 'hour' && this.datasource.to === 0) {
                await this.updateGraphData();
                this.hasData = Object.keys(this.series).length > 0;
            }
        }, 10000);
    }
    async getRoomSensorData(key) {
        try {
            const response = await this.sensorDataGetter.get(this.sensorPayload(key));
            return response.data;
        }
        catch (e) {
            Notify.create({
                message: `Error while fetching sensor data ${key.toString()}: ${e.toString()}`,
                type: `error`,
            });
        }
        return [];
    }
    async getRawRoomSensorData(key) {
        try {
            const response = await this.sensorDataGetter.getRaw(this.sensorPayload(key));
            return response.data;
        }
        catch (e) {
            Notify.create({
                message: `Error while fetching raw room sensor data ${key.toString()}: ${e.toString()}`,
                type: `error`,
            });
        }
        return [];
    }
    async getSetpointData(key) {
        try {
            const response = await this.sensorDataGetter.getSetpoints(this.sensorPayload(key));
            return response.data;
        }
        catch (e) {
            Notify.create({
                message: `Error while fetching setpoint data for ${key.toString()}: ${e.toString()}`,
                type: `error`,
            });
        }
        return [];
    }
    getRoomSensorDataSerie(key, sensorData) {
        const serie = {
            type: 'line',
            name: key.toString(),
            data: [],
        };
        for (const part of sensorData.reverse()) {
            const index = sensorData.indexOf(part);
            for (const value of part.reverse()) {
                const offset = value.time.getTimezoneOffset() * (1000 * 60);
                serie.data.push([value.time.getTime() - offset, value.value]);
            }
            if (index < sensorData.length - 1) {
                const [last] = serie.data.slice(-1);
                if (!last) {
                    continue;
                }
                serie.data.push([last[0], null]);
            }
        }
        return serie;
    }
    getRawRoomSensorDataSeries(key, data) {
        const seriesLineOptions = [];
        for (const entity of data) {
            for (const t of entity.types) {
                const label = key + t.type.replace(t.type, '');
                const serie = this.getRoomSensorDataSerie(key, t.values);
                serie.name = entity.entity.serial;
                if (label.length > 0)
                    serie.name = label + ': ' + serie.name;
                if (entity.entity.number) {
                    serie.name = serie.name + ' (' + entity.entity.number + ')';
                }
                if (t.type !== key) {
                    serie.name = serie.name + ' - ' + t.type.replace(key, '');
                }
                seriesLineOptions.push(serie);
            }
        }
        return seriesLineOptions;
    }
    get offset() {
        let unit = `h`;
        switch (this.datasource.timeframe) {
            case ETimeFrame.HOUR:
                unit = `h`;
                break;
            case ETimeFrame.DAY:
                unit = `d`;
                break;
            case ETimeFrame.WEEK:
                unit = `w`;
        }
        return [`${this.datasource.from}${unit}`, `${this.datasource.to}${unit}`];
    }
    sensorPayload(key) {
        return {
            roomId: this.room.id,
            sensor: key,
            timeFrame: this.datasource.timeframe,
            from: this.offset[0],
            to: this.offset[1],
        };
    }
};
__decorate([
    Prop({ required: true, type: Object })
], SensorGraph.prototype, "room", void 0);
__decorate([
    Prop({ required: true })
], SensorGraph.prototype, "k", void 0);
__decorate([
    Prop({ required: true })
], SensorGraph.prototype, "settings", void 0);
__decorate([
    Prop({ required: true })
], SensorGraph.prototype, "sensorDataGetter", void 0);
__decorate([
    Prop({ required: true, type: Object })
], SensorGraph.prototype, "zoomValues", void 0);
__decorate([
    Prop({ required: false, type: Boolean, default: true })
], SensorGraph.prototype, "debug", void 0);
__decorate([
    Prop({ required: false, default: { timeframe: ETimeFrame.HOUR, from: -1, to: 0 } })
], SensorGraph.prototype, "datasource", void 0);
__decorate([
    Watch('debug'),
    Watch('datasource')
], SensorGraph.prototype, "dataSourceChanged", null);
SensorGraph = __decorate([
    Component({
        components: {
            Graph,
        },
    })
], SensorGraph);
export default SensorGraph;
